import { useState } from 'react';
import { Check, Copy, Gift, Mail, MessageCircle, Send, Smartphone, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { fetchRenouvellement } from '../services/api';
import { useAuth } from '../hooks/useAuth';

/**
 * Le code de parrainage, à portée de clic.
 *
 * Il vivait dans la boutique de l'éditeur et nulle part ailleurs : une
 * association ne le connaissait que s'il lui avait été écrit un jour, ce qui
 * revient à ne pas avoir de parrainage du tout. Le mettre dans un écran de
 * réglages ne suffisait pas non plus — on parraine au moment où l'occasion se
 * présente, en discutant avec une association voisine, pas en ouvrant
 * l'administration.
 *
 * D'où sa place ici, sous le décompte de la période offerte : le seul endroit
 * visible depuis n'importe quel écran.
 *
 * Le clic ouvrait le presse-papiers, et s'arrêtait là. Or c'est sur un
 * téléphone que l'occasion se présente — au bord d'un terrain, après une
 * réunion — et coller un lien dans la bonne application demande alors quatre
 * gestes de plus. Le partage direct les supprime : le message est déjà écrit,
 * il ne reste qu'à choisir à qui l'envoyer.
 */
export default function BadgeParrainage({ className = '' }: { className?: string }) {
  const { user } = useAuth();
  const [ouvert, setOuvert] = useState(false);
  const [copie, setCopie] = useState(false);

  const { data } = useQuery({
    queryKey: ['admin-renouvellement'],
    queryFn: fetchRenouvellement,
    retry: false,
    enabled: !!user,
  });

  const renouvellement = data?.renouvellement ?? null;
  if (!renouvellement?.parrainageActif || !renouvellement.codeParrain) {
    return null;
  }

  const lien = `https://shop.gest-asso.com/commande?parrain=${encodeURIComponent(
    renouvellement.codeParrain
  )}`;

  // Le message est écrit d'avance, et il dit les deux choses qui décident :
  // ce que le logiciel fait, et ce que le parrainage donne à celui qui reçoit.
  const message =
    'Nous gérons notre association avec Gest-Asso : adhérents, événements, '
    + 'compta, bénévoles. En passant par ce lien, vous nous parrainez et nous '
    + `gagnons chacun un mois offert.\n\n${lien}`;
  const sujet = 'Une idée pour la gestion de votre association';

  const copier = () => {
    navigator.clipboard?.writeText(lien).then(
      () => setCopie(true),
      () => setCopie(false)
    );
    window.setTimeout(() => setCopie(false), 3000);
  };

  /**
   * Le partage du système, quand il existe.
   *
   * Sur téléphone il ouvre la feuille native — tous les moyens installés, y
   * compris ceux qu'on n'aurait pas pensé à lister. Sur ordinateur il n'existe
   * pas : on retombe sur nos propres boutons, qui eux marchent partout.
   */
  const partagerNatif = async () => {
    if (!navigator.share) {
      setOuvert(true);
      return;
    }
    try {
      await navigator.share({ title: sujet, text: message, url: lien });
    } catch {
      // Un partage refusé n'est pas une erreur : la personne a changé d'avis.
    }
  };

  const canaux = [
    {
      libelle: 'Courriel',
      icone: Mail,
      href: `mailto:?subject=${encodeURIComponent(sujet)}&body=${encodeURIComponent(message)}`,
    },
    {
      libelle: 'SMS',
      icone: Smartphone,
      // Le point-virgule après « sms: » est ce que reconnaissent iOS comme
      // Android : sans lui, l'un des deux ouvre un message vide.
      href: `sms:?&body=${encodeURIComponent(message)}`,
    },
    {
      libelle: 'WhatsApp',
      icone: MessageCircle,
      href: `https://wa.me/?text=${encodeURIComponent(message)}`,
    },
    {
      libelle: 'Telegram',
      icone: Send,
      href: `https://t.me/share/url?url=${encodeURIComponent(lien)}&text=${encodeURIComponent(sujet)}`,
    },
  ];

  return (
    <span className={`relative inline-flex ${className}`}>
      <button
        type="button"
        onClick={partagerNatif}
        title="Partager votre lien de parrainage"
        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 px-2.5 py-0.5 text-[0.7rem] font-semibold whitespace-nowrap hover:bg-emerald-200 dark:hover:bg-emerald-900/60 transition-colors"
      >
        <Gift className="w-3 h-3 shrink-0" aria-hidden="true" />
        Je parraine = 1 mois offert
      </button>

      {/* Le choix du moyen, quand le système n'en propose pas lui-même. */}
      {ouvert && (
        <>
          <span
            className="fixed inset-0 z-40"
            onClick={() => setOuvert(false)}
            aria-hidden="true"
          />
          <span
            role="dialog"
            aria-label="Partager le parrainage"
            className="absolute left-0 top-full mt-2 z-50 w-72 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl p-3"
          >
            <span className="flex items-center justify-between gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Partager votre parrainage
              </span>
              <button
                type="button"
                onClick={() => setOuvert(false)}
                aria-label="Fermer"
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </span>

            <span className="grid grid-cols-2 gap-2">
              {canaux.map(({ libelle, icone: Icone, href }) => (
                <a
                  key={libelle}
                  href={href}
                  target="_blank"
                  rel="noopener"
                  onClick={() => setOuvert(false)}
                  className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-2.5 py-2 text-sm text-gray-700 dark:text-gray-300 hover:border-emerald-400"
                >
                  <Icone className="w-4 h-4 shrink-0 text-emerald-600" />
                  {libelle}
                </a>
              ))}
            </span>

            <button
              type="button"
              onClick={copier}
              className="mt-2 w-full flex items-center justify-center gap-2 rounded-lg bg-gray-100 dark:bg-gray-800 px-2.5 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              {copie ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              {copie ? 'Lien copié' : 'Copier le lien'}
            </button>

            <span className="block mt-2 text-[0.7rem] font-mono text-gray-500 dark:text-gray-400 break-all">
              {lien}
            </span>
          </span>
        </>
      )}

      {/* Hors de la fenêtre de partage : la confirmation d'une copie faite
          depuis le partage natif refusé. */}
      {copie && !ouvert && (
        <span
          role="status"
          className="absolute left-0 top-full mt-1.5 z-50 w-64 rounded-lg bg-gray-900 text-white text-[0.7rem] leading-snug px-3 py-2 shadow-lg"
        >
          Lien de parrainage copié — vous pouvez le partager.
          <span className="block mt-1 font-mono opacity-80 break-all">{lien}</span>
        </span>
      )}
    </span>
  );
}
